<template>
  <div class="page boss-page work-points-page">
    <van-nav-bar title="我的工作要点" left-arrow @click-left="$router.back()"/>
    <section class="summary">
      <div><b>{{ points.length }}</b><span>本期要点</span></div>
      <div><b>{{ accepted }}</b><span>已采纳</span></div>
      <div><b>{{ completed }}</b><span>已完成</span></div>
    </section>
    <section class="task-generate">
      <header><b>从任务生成今日工作要点</b><small>选择主管已分配的任务，AI 会带入项目目标和验收标准</small></header>
      <div v-if="tasksLoading" class="task-loading"><van-loading size="20px" color="#c62828"/> 正在加载任务…</div>
      <div v-else-if="!tasks.length" class="task-empty">暂无待执行任务，请先在「我的任务」中确认是否已分配</div>
      <div v-else class="task-list">
        <button v-for="item in tasks" :key="field(item,'id')" type="button" class="task-item"
                :class="{active: String(field(item,'id')) === String(selectedTaskId)}"
                @click="selectTask(item)">
          <div class="task-item-head">
            <b>{{ field(item, 'title') }}</b>
            <span>{{ taskTypeLabel(item) }}</span>
          </div>
          <p>{{ field(item, 'objective') || '任务目标以主管分配内容为准' }}</p>
          <small v-if="field(item, 'acceptance_criteria')">验收：{{ field(item, 'acceptance_criteria') }}</small>
        </button>
      </div>
      <van-button block type="danger" round class="generate-btn"
                  :loading="generating" loading-text="AI 生成中…"
                  :disabled="generating || !selectedTaskId || tasksLoading"
                  @click="generateFromTask">
        {{ generating ? '请稍候，AI 正在生成…' : '一键生成今日工作要点' }}
      </van-button>
      <p v-if="generating" class="generate-tip">预计 30~90 秒，请勿重复点击或离开页面</p>
    </section>
    <section v-if="map.gaps && map.gaps.length" class="warning">
      <b>战略承接提醒</b>
      <p v-for="gap in map.gaps" :key="gap.nodeId">{{ gap.message }}</p>
    </section>
    <article v-for="item in points" :key="field(item,'id')">
      <header><span :class="priority(field(item,'priority'))">{{
          priorityText(field(item, 'priority'))
        }}</span><em>{{ statusText(field(item, 'status')) }}</em></header>
      <h2>{{ field(item, 'title') }}</h2>
      <dl>
        <dt>制定依据</dt>
        <dd>{{ field(item, 'rationale') }}</dd>
        <dt>目标</dt>
        <dd>{{ field(item, 'objective') }}</dd>
        <dt>完成标准</dt>
        <dd>{{ field(item, 'acceptance_criteria') }}</dd>
        <dt>协同与依赖</dt>
        <dd>{{ field(item, 'collaborator_text') || '无' }}；{{ field(item, 'dependency_text') || '无' }}</dd>
        <dt>风险</dt>
        <dd>{{ field(item, 'risk_text') || '无' }}</dd>
      </dl>
      <div v-if="field(item,'status')==='PENDING'" class="point-actions">
        <button type="button" class="point-actions-btn" @click="feedback(item,'IGNORED')">忽略</button>
        <button type="button" class="point-actions-btn" @click="editPoint(item)">修改</button>
        <button type="button" class="point-actions-btn primary" @click="feedback(item,'ACCEPTED')">直接采纳</button>
      </div>
      <div v-else-if="['ACCEPTED','MODIFIED'].includes(field(item,'status'))" class="point-actions single">
        <button type="button" class="point-actions-btn primary block" @click="complete(item)">回传完成结果</button>
      </div>
    </article>
    <van-empty v-if="!loading && !points.length" description="本期暂无工作要点"/>
    <van-dialog v-model="showEdit" title="修改并采纳工作要点" show-cancel-button confirm-button-text="保存并采纳" @confirm="confirmEdit">
      <van-field v-model.trim="edit.title" required label="要点名称"/>
      <van-field v-model.trim="edit.objective" required type="textarea" rows="2" autosize label="目标"/>
      <van-field v-model.trim="edit.acceptanceCriteria" required type="textarea" rows="2" autosize label="完成标准"/>
      <van-field v-model.trim="edit.collaborator" label="协同对象"/>
      <van-field v-model.trim="edit.dependency" label="前置依赖"/>
      <van-field v-model.trim="edit.risk" label="风险"/>
    </van-dialog>
  </div>
</template>
<script>
import api from '@/utils/coreapi';

export default {
  data() {
    return {loading: true, generating: false, tasksLoading: false, points: [], map: {}, tasks: [], selectedTaskId: '', showEdit: false, current: null, edit: {}}
  }, computed: {
    selectedTask() {
      return this.tasks.find(x => Number(this.field(x, 'id')) === Number(this.selectedTaskId))
    }, accepted() {
      return this.points.filter(x => ['ACCEPTED', 'MODIFIED'].includes(this.field(x, 'status'))).length
    }, completed() {
      return this.points.filter(x => this.field(x, 'status') === 'COMPLETED').length
    }
  }, created() {
    this.load()
  }, methods: {
    field(x, k) {
      return x[k] !== undefined ? x[k] : x[k.toUpperCase()]
    },
    taskTypeLabel(item) {
      var type = this.field(item, 'progress_type') || this.field(item, 'task_type') || '';
      return String(type).toUpperCase() === 'QUANTITATIVE' ? '可量化' : '非量化';
    },
    selectTask(item) {
      if (this.generating) return;
      this.selectedTaskId = this.field(item, 'id');
    },
    priority(v) {
      return String(v || 'normal').toLowerCase()
    }, priorityText(v) {
      return ({URGENT: '紧急', HIGH: '高', NORMAL: '普通', LOW: '低'})[v] || '普通'
    }, statusText(v) {
      return ({PENDING: '待确认', ACCEPTED: '已采纳', MODIFIED: '修改后采纳', IGNORED: '已忽略', COMPLETED: '已完成'})[v] || v
    }, today() {
      return new Date().toISOString().slice(0, 10)
    }, async load() {
      this.loading = true;
      this.tasksLoading = true;
      try {
        this.points = await api.workPoints('DAILY', this.today()) || [];
        this.tasks = await api.availableTasks() || [];
        if (this.tasks.length === 1) this.selectedTaskId = this.field(this.tasks[0], 'id');
        this.map = await api.strategyActionMap().catch(() => ({}))
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.loading = false;
        this.tasksLoading = false;
      }
    }, async generateFromTask() {
      if (this.generating || !this.selectedTaskId) return;
      this.generating = true;
      try {
        await api.generateWorkPoints({taskId: Number(this.selectedTaskId), cadence: 'DAILY', periodStart: this.today()});
        this.selectedTaskId = '';
        await this.load();
        this.$toast.success('AI 初稿已生成，请确认后执行')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.generating = false;
      }
    }, editPoint(item) {
      this.current = item;
      this.edit = {
        title: this.field(item, 'title'),
        objective: this.field(item, 'objective'),
        acceptanceCriteria: this.field(item, 'acceptance_criteria'),
        collaborator: this.field(item, 'collaborator_text') || '',
        dependency: this.field(item, 'dependency_text') || '',
        risk: this.field(item, 'risk_text') || '',
        priority: this.field(item, 'priority') || 'NORMAL'
      };
      this.showEdit = true
    }, async confirmEdit() {
      if (!this.edit.title || !this.edit.objective || !this.edit.acceptanceCriteria) return this.$toast('请补全名称、目标和完成标准');
      try {
        await api.reviseWorkPoint(this.field(this.current, 'id'), {...this.edit, revision: '员工人工修改并确认执行'});
        await this.load();
        this.$toast.success('已修改并采纳')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }, async feedback(item, status) {
      try {
        await api.feedbackWorkPoint(this.field(item, 'id'), {status, reason: status === 'IGNORED' ? '当前工作优先级不匹配' : ''});
        await this.load()
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }, async complete(item) {
      try {
        await api.feedbackWorkPoint(this.field(item, 'id'), {status: 'COMPLETED', outcome: '员工已完成，详细证据见工作日志'});
        await this.load();
        this.$toast.success('执行结果已回传')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }
  }
};
</script>
<style scoped>
@import '../boss/boss.less';
.task-generate {
  margin: .24rem .28rem;
  padding: .28rem;
  background: #fff;
  border-radius: .18rem
}

.task-generate header b, .task-generate header small {
  display: block
}

.task-generate header b {
  font-size: .31rem
}

.task-generate header small {
  margin-top: .06rem;
  color: #8b8e9e;
  font-size: .23rem;
  line-height: 1.45
}

.task-loading, .task-empty {
  margin-top: .18rem;
  padding: .28rem;
  text-align: center;
  color: #8b8e9e;
  font-size: .24rem;
  background: #f7f8fa;
  border-radius: .12rem
}

.task-list {
  margin-top: .18rem;
  display: grid;
  gap: .14rem;
  max-height: 5.2rem;
  overflow: auto
}

.task-item {
  width: 100%;
  padding: .22rem .24rem;
  text-align: left;
  border: 1px solid #eceef3;
  border-radius: .14rem;
  background: #fafbfc;
  color: #44475b
}

.task-item.active {
  border-color: #c62828;
  background: #fff5f5;
  box-shadow: 0 .04rem .16rem rgba(198, 40, 40, .12)
}

.task-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .12rem
}

.task-item-head b {
  flex: 1;
  font-size: .28rem;
  line-height: 1.4
}

.task-item-head span {
  flex: 0 0 auto;
  padding: .04rem .12rem;
  color: #1565c0;
  font-size: .2rem;
  background: #eef5ff;
  border-radius: .2rem
}

.task-item p {
  margin: .1rem 0 0;
  color: #777b90;
  font-size: .23rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden
}

.task-item small {
  display: block;
  margin-top: .08rem;
  color: #98a2b3;
  font-size: .21rem;
  line-height: 1.45
}

.generate-btn {
  margin-top: .22rem;
  height: .88rem;
  font-size: .3rem;
  font-weight: 600
}

.generate-tip {
  margin: .14rem 0 0;
  text-align: center;
  color: #c62828;
  font-size: .22rem
}

.task-generate .van-cell {
  margin-top: .18rem;
  padding-left: 0;
  padding-right: 0
}

.task-generate .van-button {
  margin-top: .18rem
}

.task-preview {
  padding: .18rem;
  color: #555;
  background: #f7f8fa;
  border-radius: .08rem
}

.task-preview b {
  font-size: .27rem
}

.task-preview p {
  margin: .08rem 0 0;
  color: #7f8392;
  font-size: .23rem;
  line-height: 1.5
}

.page {
  min-height: 100vh;
  padding-bottom: 1.2rem;
  background: #f6f7fb;
  color: #34364a
}

.summary {
  margin: .28rem;
  display: grid;
  grid-template-columns:repeat(3, 1fr);
  background: #fff;
  border-radius: .18rem
}

.summary div {
  padding: .28rem;
  text-align: center
}

.summary b, .summary span {
  display: block
}

.summary b {
  font-size: .4rem;
  color: #c62828
}

.summary span {
  margin-top: .06rem;
  color: #8b8e9e;
  font-size: .24rem
}

.generate {
  display: flex;
  gap: .12rem;
  align-items: center;
  margin: .24rem .28rem
}

.generate select {
  flex: 1;
  height: .72rem;
  border: 1px solid #ddd;
  border-radius: .08rem;
  background: #fff;
  color: #555;
  font-size: .25rem
}

.generate .van-button {
  flex: 1;
  font-size: .25rem
}

.warning, article {
  margin: .24rem .28rem;
  padding: .3rem;
  background: #fff;
  border-radius: .18rem
}

.warning {
  border-left: .06rem solid #e09a2d
}

.warning b {
  font-size: .3rem
}

.warning p {
  margin: .1rem 0 0;
  color: #777b8c;
  font-size: .25rem
}

article header {
  display: flex;
  justify-content: space-between;
  align-items: center
}

article header span {
  padding: .05rem .12rem;
  border-radius: .06rem;
  background: #eef0f5;
  font-size: .23rem
}

article header .urgent, article header .high {
  color: #b91c1c;
  background: #fff0f0
}

article header em {
  color: #888b9a;
  font-size: .23rem;
  font-style: normal
}

h2 {
  margin: .2rem 0;
  font-size: .34rem
}

dl {
  margin: 0
}

dt {
  margin-top: .16rem;
  color: #8d90a0;
  font-size: .23rem
}

dd {
  margin: .05rem 0 0;
  font-size: .27rem;
  line-height: 1.55
}

.point-actions {
  margin-top: .28rem;
  display: flex;
  align-items: stretch;
  border: 1px solid #c62828;
  border-radius: .14rem;
  overflow: hidden;
  background: #fff;
}

.point-actions.single {
  border: 0;
  background: transparent;
}

.point-actions-btn {
  flex: 1;
  min-width: 0;
  min-height: .8rem;
  padding: .18rem .2rem;
  border: 0;
  border-right: 1px solid rgba(198, 40, 40, .28);
  background: #fff;
  color: #c62828;
  font-size: .26rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  transition: background .15s, color .15s;
}

.point-actions-btn:last-child {
  border-right: 0;
}

.point-actions-btn:active {
  background: #fff5f5;
}

.point-actions-btn.primary {
  flex: 1.15;
  background: linear-gradient(135deg, #c62828, #d84343);
  color: #fff;
  border-right: 0;
}

.point-actions-btn.primary:active {
  background: linear-gradient(135deg, #b71c1c, #c62828);
}

.point-actions-btn.block {
  width: 100%;
  min-height: .88rem;
  border: 0;
  border-radius: .14rem;
  font-size: .28rem;
  font-weight: 600;
  box-shadow: 0 .06rem .2rem rgba(198, 40, 40, .18);
}

.point-actions.single .point-actions-btn.block {
  flex: none;
}

.work-points-page { margin: 0; min-height: 100vh; background: #f6f7fb; padding-bottom: 1.7rem; }
.work-points-page /deep/ .van-nav-bar { margin: 0; width: 100%; height: 1.22667rem; border-radius: 0; background: linear-gradient(135deg,#351b1d,#c6282d); }
.work-points-page .summary, .work-points-page .task-generate, .work-points-page article, .work-points-page .warning { box-shadow: 0 .06rem .24rem rgba(30,35,60,.06); }
</style>



// WEBPACK FOOTER //
// src/views/efficiency/WorkPoints.vue